import _Dialog from "@mui/material/Dialog";
import _DialogActions from "@mui/material/DialogActions";
import _DialogContent from "@mui/material/DialogContent";
import _Tooltip from "@mui/material/Tooltip";
import _AddPhotoAlternate from "@mui/icons-material/AddPhotoAlternate";
import _Box from "@mui/material/Box";
import _IconButton from "@mui/material/IconButton";
import _Close from "@mui/icons-material/Close";
import _Card from "@mui/material/Card";
import _CardMedia from "@mui/material/CardMedia";
import _TextField from "@mui/material/TextField";
import _DialogTitle from "@mui/material/DialogTitle";
function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import { Report } from '../../models';
import { ReportService, RouteReportService } from '../../services';
import { Button } from '../button';
export function NewReviewFormDialog({
  canClose,
  report,
  onClose,
  ...props
}) {
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const photosInputRef = useRef(null);
  const canAttachPhotos = report instanceof Report;
  const {
    isSubmitting,
    values,
    touched,
    errors,
    setValues,
    setFieldValue,
    setTouched,
    handleChange,
    handleSubmit
  } = useFormik({
    initialValues: {
      description: '',
      close: false
    },
    validationSchema: Yup.object().shape({
      // texte optionnel si au moins une photo est jointe (report uniquement, pas de photo sur les route reports)
      description: Yup.string().test('required-unless-photo', 'required', value => canAttachPhotos && selectedPhotos.length > 0 || Boolean(value && value.length >= 10))
    }),
    onSubmit
  });
  const {
    t
  } = useTranslation();
  const {
    enqueueSnackbar
  } = useSnackbar();
  useEffect(() => {
    if (props.open) {
      setValues({
        description: '',
        close: false
      });
      setTouched({
        description: false
      });
      clearSelectedPhotos();
    }
  }, [props.open]);
  useEffect(() => {
    return () => clearSelectedPhotos();
  }, []);
  function clearSelectedPhotos() {
    setSelectedPhotos(prevPhotos => {
      prevPhotos.forEach(({
        preview
      }) => URL.revokeObjectURL(preview));
      return [];
    });
  }
  function handlePhotosInputChange({
    target: {
      files
    }
  }) {
    if (!files) return;
    const time = new Date().getTime();
    setSelectedPhotos([...selectedPhotos, ...Array.from(files).map((file, index) => ({
      id: `${time}-${index}`,
      file,
      preview: URL.createObjectURL(file)
    }))]);
  }
  async function onSubmit({
    description,
    isValid,
    close
  }, {
    setSubmitting
  }) {
    setSubmitting(true);
    try {
      let updatedReport = report.clone();
      if (updatedReport instanceof Report) {
        const newReview = await ReportService.addReview({
          reportId: report.id,
          comment: description,
          rating: undefined,
          photos: selectedPhotos.map(({
            file
          }) => file)
        });
        clearSelectedPhotos();
        if (close) updatedReport = await ReportService.updateReport(updatedReport, {
          status: 'CLOSED',
          isValid
        });else updatedReport.reviews.push(newReview);
      } else {
        const data = close ? {
          response: description,
          status: 'CLOSED'
        } : {
          response: description
        };
        updatedReport = await RouteReportService.updateRouteReport(report.id, data);
      }
      onClose(updatedReport);
    } catch {
      enqueueSnackbar?.(t('commons.report.reviews.not_added'));
    }
    setSubmitting(false);
  }
  return /*#__PURE__*/React.createElement(_Dialog, _extends({
    fullWidth: true,
    disableEscapeKeyDown: isSubmitting,
    maxWidth: "sm",
    onClose: (_, reason) => reason !== 'backdropClick' && onClose(),
    sx: {
      '.MuiPaper-root': {
        borderRadius: 4
      }
    }
  }, props), /*#__PURE__*/React.createElement("form", {
    onSubmit: handleSubmit
  }, /*#__PURE__*/React.createElement(_DialogTitle, null, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: `commons.${report instanceof Report ? 'report.review_dialog' : 'route_report.response_dialog'}.title`
  })), /*#__PURE__*/React.createElement(_DialogContent, null, /*#__PURE__*/React.createElement(_TextField, {
    fullWidth: true,
    multiline: true,
    disabled: isSubmitting,
    error: touched.description && Boolean(errors.description),
    id: "description",
    label: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: `commons.${report instanceof Report ? 'report.reviews' : 'route_report.response_dialog'}.tooltip`
    }),
    margin: "dense",
    name: "description",
    onChange: handleChange,
    rows: 3,
    slotProps: {
      htmlInput: {
        min: 10
      }
    },
    value: values.description,
    variant: "outlined"
  }), canAttachPhotos && /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexWrap: "wrap",
    gap: 2,
    marginTop: 2
  }, selectedPhotos.map(({
    id,
    preview
  }, index) => /*#__PURE__*/React.createElement(_Box, {
    key: id,
    position: "relative"
  }, /*#__PURE__*/React.createElement(_Card, {
    sx: {
      borderRadius: 2
    },
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(_CardMedia, {
    component: "img",
    src: preview,
    sx: {
      height: 64,
      objectFit: 'cover',
      width: 64
    }
  })), /*#__PURE__*/React.createElement(_IconButton, {
    disabled: isSubmitting,
    onClick: () => {
      const updatedPhotos = [...selectedPhotos];
      URL.revokeObjectURL(updatedPhotos[index].preview);
      updatedPhotos.splice(index, 1);
      setSelectedPhotos(updatedPhotos);
    },
    size: "small",
    sx: {
      bgcolor: 'background.paper',
      boxShadow: 1,
      position: 'absolute',
      right: -8,
      top: -8,
      '&:hover': {
        bgcolor: 'background.paper'
      }
    }
  }, /*#__PURE__*/React.createElement(_Close, {
    fontSize: "small"
  })))), /*#__PURE__*/React.createElement(_Tooltip, {
    title: /*#__PURE__*/React.createElement(Trans, {
      i18nKey: "commons.actions.add_photo"
    })
  }, /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement(_IconButton, {
    disabled: isSubmitting,
    onClick: () => photosInputRef.current?.click()
  }, /*#__PURE__*/React.createElement(_AddPhotoAlternate, {
    color: "action"
  })))), /*#__PURE__*/React.createElement("input", {
    multiple: true,
    accept: "image/*",
    onChange: handlePhotosInputChange,
    onClick: ({
      currentTarget
    }) => currentTarget.value = '',
    ref: photosInputRef,
    style: {
      display: 'none'
    },
    type: "file"
  }))), /*#__PURE__*/React.createElement(_DialogActions, null, /*#__PURE__*/React.createElement(Button, {
    disabled: isSubmitting,
    onClick: () => onClose(),
    size: "small",
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.close"
  })), report.status === 'OPEN' && canClose && /*#__PURE__*/React.createElement(Button, {
    disabled: isSubmitting,
    onClick: () => {
      setFieldValue('close', true);
      handleSubmit();
    },
    size: "small",
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.report.review_dialog.actions.send_and_close"
  })), report.status === 'OPEN' && canClose && /*#__PURE__*/React.createElement(Button, {
    disabled: isSubmitting,
    onClick: () => {
      setFieldValue('close', true);
      setFieldValue('isValid', true);
      handleSubmit();
    },
    size: "small",
    variant: "outlined"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.report.review_dialog.actions.send_reward_and_close"
  })), /*#__PURE__*/React.createElement(Button, {
    color: "primary",
    disabled: isSubmitting,
    size: "small",
    type: "submit",
    variant: "contained"
  }, /*#__PURE__*/React.createElement(Trans, {
    i18nKey: "commons.actions.send"
  })))));
}